import React, { useEffect, useState } from "react";
import {
  FaUserCircle,
  FaSignOutAlt,
  FaIdCard,
  FaCertificate,
  FaEdit,
  FaTimes,
  FaCloudUploadAlt,
} from "react-icons/fa";
import Login from "../components/Login";
import apiRequest from "../services/api";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);

  /* ==========================
      LOAD USER FROM STORAGE
  ========================== */
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (!storedUser || !token) {
        setShowLoginModal(true);
        setUser(null);
        return;
      }

      setUser(JSON.parse(storedUser));
      setShowLoginModal(false);
    } catch {
      localStorage.clear();
      setShowLoginModal(true);
    }
  }, []);

  const handleAmendSuccess = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  /* ==========================
      LOGIN REQUIRED
  ========================== */
  if (showLoginModal) {
    return (
      <Login
        onClose={() => setShowLoginModal(false)}
        onLogin={(data) => {
          setUser(data);
          setShowLoginModal(false);
        }}
      />
    );
  }

  if (!user) {
    return (
      <div className="p-10 text-center text-gray-500">
        No profile data available
      </div>
    );
  }

  /* ==========================
      RENDER PROFILE
  ========================== */
  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg border overflow-hidden">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-300 to-blue-100 p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {user.photoUrl ? (
              <img
                src={user.photoUrl}
                alt="Profile"
                className="w-20 h-20 rounded-full border-4 border-white object-cover"
              />
            ) : (
              <FaUserCircle className="text-white text-7xl" />
            )}
            <div className="text-white">
              <h2 className="text-2xl font-bold">
                {user.username || "User Profile"}
              </h2>
              <p className="text-sm opacity-90">{user.name || "—"}</p>
            </div>
          </div>

          <button
            onClick={() => setShowAmendModal(true)}
            className="flex items-center gap-2 bg-white text-sky-700 hover:bg-sky-50 font-bold px-4 py-2.5 rounded-xl shadow transition-all active:scale-95 text-sm"
          >
            <FaEdit /> Edit Profile
          </button>
        </div>

        {/* BODY */}
        <div className="p-8 space-y-10">
          {/* BASIC INFO */}
          <Section title="Basic Information">
            <Grid>
              <ProfileItem label="ID Number" value={user.idno} />
              <ProfileItem label="Mobile" value={user.mobile} />
              <ProfileItem label="Email" value={user.email} />
              <ProfileItem label="Status" value={user.status} />
              <ProfileItem
                label="Institution"
                value={user.college?.description || "~ÑA~"}
              />
              <ProfileItem
                label="Department"
                value={user.department?.description || "~ÑA~"}
              />
            </Grid>
          </Section>

          {/* FAYDA DETAILS SECTION */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-6 mb-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 border-b border-slate-200 pb-3 gap-3">
              <h4 className="text-[10px] md:text-xs font-bold uppercase text-slate-500 tracking-widest flex items-center gap-2">
                Identity Verification Data From FAYDA
              </h4>
            </div>

            {(() => {
              const details = user.faydaDetails || "";
              const getValue = (key, str) => {
                const regex = new RegExp(
                  `${key}=(?:'|Address{)?(.*?)(?:'|\\s?,|\\s?})`,
                  "i"
                );
                const match = str.match(regex);
                return match ? match[1] : "N/A";
              };

              const userData = {
                name: getValue("name", details),
                gender: getValue("gender", details),
                birthdate: getValue("birthdate", details),
                nationality: getValue("nationality", details),
                email: getValue("email", details),
                phone: getValue("phone", details),
                region: getValue("region", details),
                zone: getValue("zone", details),
                woreda: getValue("woreda", details),
              };

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8 text-sm">
                  {/* Column 1: Primary Identity */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                        Full Name
                      </label>
                      <p className="text-slate-900 font-bold text-base leading-tight">
                        {userData.name}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                          Gender
                        </label>
                        <p className="text-slate-600 font-mono text-[10px] break-all bg-white p-2 rounded border border-slate-100">
                          {userData.gender}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Column 2: Personal Details */}
                  <div className="space-y-4 md:border-l border-slate-200 md:pl-6">
                    <div className="flex justify-between gap-4">
                      <div className="flex-1 text-right md:text-left">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                          Birth Date
                        </label>
                        <p className="text-slate-700 font-medium">
                          {userData.birthdate}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                        Nationality
                      </label>
                      <p className="text-slate-700 font-medium">
                        {userData.nationality}
                      </p>
                    </div>
                  </div>

                  {/* Column 3: Contact & Location */}
                  <div className="space-y-4 md:border-l border-slate-200 md:pl-6">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                        Contact
                      </label>
                      <p className="text-slate-800 font-medium">
                        {userData.phone}
                      </p>
                      <p className="text-sky-600 text-xs break-all hover:underline cursor-pointer">
                        {userData.email}
                      </p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">
                        Registered Address
                      </label>
                      <p className="text-slate-600 text-xs leading-relaxed italic">
                        {userData.region}, {userData.zone}, {userData.woreda}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* DOCUMENTS */}
          <Section title="Documents">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DocumentCard
                title="Profile Photo"
                icon={<FaUserCircle />}
                url={user.photoUrl}
              />

              <DocumentCard
                title="Academic Certificate"
                icon={<FaCertificate />}
                url={user.certUrl}
              />
            </div>
          </Section>
        </div>
      </div>

      {/* AMENDMENT MODAL */}
      <AmendmentModal
        isOpen={showAmendModal}
        onClose={() => setShowAmendModal(false)}
        user={user}
        onSuccess={handleAmendSuccess}
      />
    </section>
  );
};

/* ==========================
    AMENDMENT MODAL COMPONENT
========================== */

const AmendmentModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [photoFile, setPhotoFile] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [certPreviewName, setCertPreviewName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  if (!isOpen) return null;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCertChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertFile(file);
      setCertPreviewName(file.name);
    }
  };

  const handleClose = () => {
    setPhotoFile(null);
    setCertFile(null);
    setPhotoPreview(null);
    setCertPreviewName("");
    setError("");
    setSuccessMsg("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!photoFile && !certFile) {
      return setError("Please select at least one document to upload.");
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();

      // Multipart parts match: @RequestPart("photo") & @RequestPart("cert")
      if (photoFile) formData.append("photo", photoFile);
      if (certFile) formData.append("cert", certFile);

      // Path variable maps to: /documents/{username}
      const username = user?.username || user?.idno;

      const response = await apiRequest(`users/documents/${username}`, {
        method: "POST",
        body: formData,
        isFormData: true,
      });

      setSuccessMsg("Documents uploaded successfully!");

      if (onSuccess) {
        onSuccess(response?.data || response);
      }

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to upload documents. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4 backdrop-blur-md">
      <div className="relative bg-white rounded-3xl w-full max-w-lg p-6 md:p-8 shadow-2xl border border-sky-100">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2 rounded-full transition-all"
        >
          <FaTimes className="text-lg" />
        </button>

        <h3 className="text-xl font-black text-slate-800 mb-1">
          Upload Documents
        </h3>
        <p className="text-xs text-slate-500 mb-6">
          Upload Academic certificate
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl border border-red-100">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-sky-50 text-sky-700 text-xs font-bold rounded-xl border border-sky-100">
            {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* CERTIFICATE FIELD */}
          <div>
            <label className="block text-xs font-bold text-sky-700 uppercase mb-2">
              Certificate Document
            </label>
            <label className="cursor-pointer bg-slate-50 hover:bg-sky-50 border-2 border-dashed border-slate-200 hover:border-sky-300 p-4 rounded-xl flex flex-col items-center justify-center gap-2 text-xs font-bold text-slate-600 transition-all">
              <FaCertificate className="text-2xl text-sky-500" />
              <span>
                {certPreviewName ? certPreviewName : "Choose Certificate File"}
              </span>
              <span className="text-[10px] text-slate-400 font-normal">
                PDF or image files accepted
              </span>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={handleCertChange}
                className="hidden"
              />
            </label>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="w-1/2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold py-3 rounded-xl transition-all text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-1/2 bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-xl shadow-lg transition-all active:scale-95 text-sm"
            >
              {isSubmitting ? "Uploading..." : "Upload Files"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ==========================
    REUSABLE COMPONENTS
========================== */

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-lg font-bold text-gray-700 mb-4 border-b pb-2">
      {title}
    </h3>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const ProfileItem = ({ label, value }) => (
  <div className="bg-gray-50 p-4 rounded-lg border">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-800 break-all">{value ?? "—"}</p>
  </div>
);

const DocumentCard = ({ title, icon, url }) => (
  <div className="border rounded-xl p-4 bg-gray-50 hover:shadow transition">
    <div className="flex items-center gap-2 mb-3 text-gray-700 font-semibold">
      {icon}
      {title}
    </div>

    {url ? (
      <>
        <img
          src={url}
          alt={title}
          onError={(e) => (e.target.style.display = "none")}
          className="w-full h-40 object-contain bg-white border rounded mb-3"
        />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 text-sm hover:underline"
        >
          View / Download
        </a>
      </>
    ) : (
      <p className="text-sm text-gray-400">Not uploaded</p>
    )}
  </div>
);

export default Profile;
